import { ReactNode } from "react";

import { CompositeCalloutProps } from "./compositeCallout";
import { ConfirmationCardProps } from "./confirmationCard";
import { ValueOf } from "./utility";
import { ZendeskField } from "./zendesk";

export interface FormContainerCommonProps {
  children: ReactNode;
  formIcon: ReactNode;
  header: string;
  id?: string;
  loading: boolean;
  onSubmit: () => void;
  submitValue: string;
  subText?: string;
}

export interface FormContainerStaticProps extends FormContainerCommonProps {
  maxHeight?: never;
  isCollapsed?: never;
  onToggleCollapse?: never;
}

export interface FormContainerCollapsibleProps
  extends FormContainerCommonProps {
  maxHeight: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export type FormContainerProps =
  | FormContainerStaticProps
  | FormContainerCollapsibleProps;

/**
 * TICKET SUBMISSION VIEW
 */
export const TicketSubmissionVariantsEnum = {
  oneSupportCenterGated: "oneSupportCenterGated",
  oneSupportCenterUngated: "oneSupportCenterUngated",
} as const;
export type TicketSubmissionVariantsEnumType = ValueOf<
  typeof TicketSubmissionVariantsEnum
>;

export interface TicketSubmissionSuccessDetails {
  confirmationProps: ConfirmationCardProps;
  shouldHideForm?: boolean;
  wasSuccessful?: boolean;
}

export interface TicketSubmissionResponse<T> {
  data: T;
  message?: string;
  success: boolean;
  statusCode: number;
}

export type TicketSubmissionFormValues = OneSupportCenterUnprocessedFormValues;

export interface TicketSubmissionCommonProps {
  loading?: boolean;
  onSubmit: (formData: TicketSubmissionFormValues) => Promise<void>;
  originUrl?: string;
  shouldBlockHistory?: boolean;
  successDetails?: TicketSubmissionSuccessDetails;
  userInput?: string;
  comment?: string;
  calloutProps?: CompositeCalloutProps;
  followUpQuestions?: string[];
  product?: string;
  issueArea?: string;
}

export interface TicketSubmissionOneSupportCenterFormProps
  extends TicketSubmissionCommonProps {
  onCompletePersonalInformation?: never;
  priorityLevels: ZendeskField[];
  products?: ZendeskField[];
  variant: TicketSubmissionVariantsEnumType;
}

export type TicketSubmissionProps = TicketSubmissionOneSupportCenterFormProps;

/**
 * ONE SUPPORT CENTER
 */
export interface OneSupportCenterFormProps {
  calloutProps?: CompositeCalloutProps;
  comment?: string;
  loading: boolean;
  userInput?: string;
  priorityLevels: ZendeskField[];
  onSubmit: (formData: OneSupportCenterFormValues) => Promise<void>;
  followUpQuestions?: string[];
  product?: string;
  issueArea?: string;
}

export interface OneSupportCenterBaseFormValues {
  attachments?: File[];
  comment: string;
  priorityLevel: string;
  subject: string;
  email: string;
  collaborators?: string[];
}

export interface OneSupportCenterUnprocessedFormValues
  extends OneSupportCenterBaseFormValues {
  followUpQuestions?: string[];
  followUpAnswers?: string[];
  product?: string;
  issueArea?: string;
}
export interface OneSupportCenterFormValues
  extends OneSupportCenterBaseFormValues {
  followUpData?: string[];
  product?: string;
  issueArea?: string;
}

export enum OneSupportCenterFormValueType {
  attachments = "attachments",
  comment = "comment",
  email = "email",
  collaborators = "collaborators",
  priorityLevel = "priorityLevel",
  subject = "subject",
  followUpQuestions = "followUpQuestions",
  followUpAnswers = "followUpAnswers",
  product = "product",
  issueArea = "issueArea",
}

export interface LegalStatementProps {
  className?: string;
}
